import React from 'react';
import { BookingTheme, FLOW_DEFAULTS } from './themes';

/**
 * ConfirmationPass — the confirmation scene: "confirming" check
 * animation → pass peeking from a slot ("tray") → full-screen
 * brand pass ("expanded").
 *
 * Matched against the Claude Design prototype (pass.jsx + flow.jsx in
 * design-imports/). The design's fixed 402x874 phone-frame geometry is
 * adapted to arbitrary viewports: vertical positions anchored to the
 * bottom edge, pass width capped at the design's 340px.
 */

// ─── Types ────────────────────────────────────────────────────────

export interface PassBooking {
  service: string;
  dateNum: string;
  dateDay: string;
  time: string;
  duration: string;
  guest: string | null;
  withName?: string | null;
  confirmation: string;
}

interface ConfirmSceneProps {
  stage: 'confirming' | 'tray' | 'expanded';
  theme: BookingTheme;
  visible: boolean;
  booking?: PassBooking | null;
  onTap: () => void;
  onClose: () => void;
  onAddToCalendar?: () => void;
  onManage?: () => void;
  businessName: string;
  businessTagline?: string | null;
}

interface PassProps {
  stage: 'confirming' | 'tray' | 'expanded';
  theme: BookingTheme;
  onTap: () => void;
  onClose: () => void;
  onAddToCalendar?: () => void;
  onManage?: () => void;
  booking?: PassBooking | null;
  businessName: string;
  businessTagline?: string | null;
}

// ─── Easing constants (design: "Lift & Reveal" springs) ──────────

const SPRING = 'cubic-bezier(0.34, 1.28, 0.44, 1)';
const SPRING_SOFT = 'cubic-bezier(0.34, 1.12, 0.5, 1)';
const EASE_SOFT = 'cubic-bezier(0.22, 1, 0.36, 1)';

const INK = 'var(--f-ink, rgba(28,18,12,0.9))';
const INK_SOFT = 'var(--f-ink-soft, rgba(28,18,12,0.55))';
const INK_FAINT = 'var(--f-ink-faint, rgba(28,18,12,0.30))';
const HAIRLINE = 'var(--f-hairline, rgba(28,18,12,0.08))';

// ─── TapHint ──────────────────────────────────────────────────────

export function TapHint() {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 16px',
        borderRadius: 999,
        background: HAIRLINE,
        border: `0.5px solid ${HAIRLINE}`,
        fontSize: 12.5,
        color: INK_SOFT,
        letterSpacing: '-0.005em',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        animation: 'tapHintPulse 2.2s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes tapHintPulse {
          0%, 100% { opacity: 0.85; }
          50% { opacity: 1; }
        }
      `}</style>
      <div style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: `1px solid ${INK_FAINT}`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: INK_SOFT,
        }} />
      </div>
      Tap anywhere to view booking
    </div>
  );
}

// ─── ConfirmingState — drawing circle + check ─────────────────────

function ConfirmingState({ visible }: { visible: boolean }) {
  // Force a paint with the strokes hidden before drawing them in
  const [drawn, setDrawn] = React.useState(false);
  React.useEffect(() => {
    if (!visible) { setDrawn(false); return; }
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setDrawn(true)));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 28,
      padding: '0 40px',
      opacity: visible ? 1 : 0,
      transition: 'opacity 380ms ease-out',
      pointerEvents: 'none',
      zIndex: 10,
    }}>
      <div style={{
        width: 72,
        height: 72,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <svg width="72" height="72" viewBox="0 0 72 72" style={{ position: 'absolute', inset: 0 }}>
          <circle cx="36" cy="36" r="34" stroke={HAIRLINE} strokeWidth="1.5" fill="none" />
          <circle
            cx="36" cy="36" r="34"
            stroke={INK} strokeWidth="1.5" fill="none"
            strokeLinecap="round"
            strokeDasharray="213.6"
            strokeDashoffset={drawn ? 0 : 213.6}
            transform="rotate(-90 36 36)"
            style={{
              transition: `stroke-dashoffset 850ms cubic-bezier(0.5, 0, 0.2, 1) ${drawn ? '120ms' : '0ms'}`,
            }}
          />
          <path
            d="M21 37 L31 47 L51 26"
            stroke={INK} strokeWidth="1.7" fill="none"
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray="46"
            strokeDashoffset={drawn ? 0 : 46}
            style={{
              transition: `stroke-dashoffset 480ms cubic-bezier(0.4, 0, 0.2, 1) ${drawn ? '780ms' : '0ms'}`,
            }}
          />
        </svg>
      </div>
      <div style={{
        fontFamily: '"Instrument Serif", "Times New Roman", serif',
        fontStyle: 'italic',
        fontSize: 26,
        color: INK,
        letterSpacing: '-0.01em',
        opacity: drawn ? 1 : 0,
        transform: drawn ? 'translateY(0)' : 'translateY(6px)',
        transition: `opacity 400ms ease ${drawn ? '900ms' : '0ms'}, transform 500ms ${EASE_SOFT} ${drawn ? '900ms' : '0ms'}`,
      }}>Booking confirmed</div>
    </div>
  );
}

// ─── TrayChrome — headline + tap hint around the peeking pass ─────

function TrayChrome({ visible }: { visible: boolean }) {
  return (
    <>
      <div style={{
        position: 'absolute',
        top: 96,
        left: 0,
        right: 0,
        textAlign: 'center',
        padding: '0 40px',
        zIndex: 12,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-6px)',
        transition: `opacity 500ms ease ${visible ? '220ms' : '0ms'}, transform 700ms ${EASE_SOFT} ${visible ? '220ms' : '0ms'}`,
      }}>
        <div style={{
          fontFamily: '"Instrument Serif", "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 30,
          color: INK,
          letterSpacing: '-0.015em',
          lineHeight: 1.05,
        }}>You&rsquo;re booked in.</div>
        <div style={{
          marginTop: 10,
          fontSize: 13.5,
          color: INK_SOFT,
          letterSpacing: '-0.005em',
          fontWeight: 450,
        }}>A confirmation pass has been issued.</div>
      </div>

      <div style={{
        position: 'absolute',
        bottom: 90,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 40,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: `opacity 500ms ease ${visible ? '700ms' : '0ms'}, transform 600ms ${EASE_SOFT} ${visible ? '700ms' : '0ms'}`,
      }}>
        <TapHint />
      </div>
    </>
  );
}

// ─── Shelf + Slot ─────────────────────────────────────────────────

function Shelf({ visible, bg }: { visible: boolean; bg: string }) {
  return (
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 206,
      background: bg,
      boxShadow: 'inset 0 1px 0 rgba(0,0,0,0.04)',
      zIndex: 35,
      opacity: visible ? 1 : 0,
      transition: `opacity 380ms ${EASE_SOFT}`,
      pointerEvents: 'none',
    }} />
  );
}

function Slot({ visible }: { visible: boolean }) {
  return (
    <>
      <div style={{
        position: 'absolute',
        bottom: 200,
        left: '50%',
        transform: `translateX(-50%) scaleX(${visible ? 1 : 0.5})`,
        transformOrigin: '50% 50%',
        width: 240,
        height: 6,
        borderRadius: 3,
        background: 'rgba(28,18,12,0.78)',
        boxShadow:
          'inset 0 1.5px 1.5px rgba(0,0,0,0.7), ' +
          'inset 0 -0.5px 0 rgba(255,255,255,0.06), ' +
          '0 1px 0 rgba(255,255,255,0.85)',
        opacity: visible ? 1 : 0,
        transition: `opacity 420ms ${EASE_SOFT}, transform 520ms cubic-bezier(0.34,1.25,0.5,1)`,
        zIndex: 38,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: 188,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 320,
        height: 24,
        borderRadius: '50%',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(28,18,12,0.10), rgba(28,18,12,0) 70%)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 500ms ease-out',
        zIndex: 36,
        pointerEvents: 'none',
      }} />
    </>
  );
}

// ─── Pass header pieces ───────────────────────────────────────────

function FiggyMark({ theme }: { theme: BookingTheme }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <span style={{
        fontFamily: theme.display,
        fontStyle: theme.displayItalic ? 'italic' : 'normal',
        fontWeight: theme.displayWeight,
        fontSize: 22,
        lineHeight: 1,
        color: theme.fg,
        letterSpacing: '-0.01em',
      }}>figgy</span>
      <span style={{
        fontFamily: theme.display,
        fontStyle: theme.displayItalic ? 'italic' : 'normal',
        fontWeight: theme.displayWeight,
        fontSize: 22,
        lineHeight: 1,
        color: theme.fg,
      }}>.</span>
    </div>
  );
}

function ConfirmedPill({ theme }: { theme: BookingTheme }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      padding: '6px 11px 6px 9px',
      borderRadius: 999,
      background: theme.accentSoft,
      border: `0.5px solid ${theme.border}`,
      fontSize: 10,
      letterSpacing: '0.16em',
      textTransform: 'uppercase' as const,
      fontWeight: 600,
      color: theme.fg,
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d="M1.5 5.2L4 7.5 9 2" stroke={theme.fg} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Confirmed
    </div>
  );
}

function PassHeader({ theme, expanded, onClose, businessName, businessTagline, confirmation }: {
  theme: BookingTheme;
  expanded: boolean;
  onClose: () => void;
  businessName: string;
  businessTagline?: string | null;
  confirmation?: string;
}) {
  return (
    <div style={{ padding: '0 28px', position: 'relative', zIndex: 1 }}>
      {/* top row: figgy mark + confirmed pill / close button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 22,
      }}>
        <FiggyMark theme={theme} />
        <div style={{ position: 'relative', width: 110, height: 30 }}>
          {/* confirmed pill — recedes when close shows */}
          <div style={{
            position: 'absolute',
            right: 0,
            top: 0,
            opacity: expanded ? 0 : 1,
            transform: expanded ? 'scale(0.9)' : 'scale(1)',
            transformOrigin: 'right center',
            transition: `opacity 250ms ${EASE_SOFT}, transform 250ms ${EASE_SOFT}`,
            pointerEvents: expanded ? 'none' : 'auto',
          }}>
            <ConfirmedPill theme={theme} />
          </div>
          {/* close button — visible expanded */}
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Close"
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: theme.accentSoft,
              border: `1px solid ${theme.border}`,
              color: theme.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              opacity: expanded ? 1 : 0,
              transform: expanded ? 'scale(1)' : 'scale(0.85)',
              transition: `opacity 250ms ${EASE_SOFT} ${expanded ? '450ms' : '0ms'}, transform 350ms ${SPRING_SOFT} ${expanded ? '450ms' : '0ms'}`,
              pointerEvents: expanded ? 'auto' : 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11">
              <path d="M1 1l9 9M10 1l-9 9" stroke={theme.fg} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tagline (kicker) */}
      {businessTagline && (
        <div style={{
          fontSize: 10.5,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.24em',
          color: theme.fg,
          opacity: 0.62,
          marginBottom: 14,
          fontWeight: 500,
        }}>{businessTagline}</div>
      )}

      {/* Brand display name */}
      <div style={{
        fontFamily: theme.display,
        fontWeight: theme.displayWeight,
        fontStyle: theme.displayItalic ? 'italic' : 'normal',
        fontSize: 46,
        lineHeight: 0.96,
        letterSpacing: '-0.015em',
        marginBottom: 14,
      }}>{businessName}</div>

      {/* meta line under the name */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12.5,
        opacity: 0.7,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}>
        <span>Booking pass</span>
        {confirmation && (
          <>
            <span style={{ opacity: 0.5 }}>·</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{confirmation}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Perforation — dashed line + edge scallops ────────────────────

function Perforation({ theme }: { theme: BookingTheme }) {
  return (
    <div style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center' }}>
      <div style={{
        position: 'absolute',
        left: -7,
        top: 0,
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.18)',
        boxShadow: 'inset -1px 0 1px rgba(0,0,0,0.15)',
      }} />
      <div style={{
        position: 'absolute',
        right: -7,
        top: 0,
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.18)',
        boxShadow: 'inset 1px 0 1px rgba(0,0,0,0.15)',
      }} />
      <div style={{
        flex: 1,
        margin: '0 18px',
        height: 1,
        backgroundImage: `repeating-linear-gradient(to right, ${theme.fg} 0 4px, transparent 4px 9px)`,
        opacity: 0.35,
      }} />
    </div>
  );
}

// ─── PassDetails — service, date/time, guest ──────────────────────

function PassLabel({ theme, children }: { theme: BookingTheme; children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 9.5,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.22em',
      opacity: 0.55,
      fontWeight: 600,
      color: theme.fg,
    }}>{children}</div>
  );
}

function PassDetails({ theme, expanded, booking }: {
  theme: BookingTheme;
  expanded: boolean;
  booking?: PassBooking | null;
}) {
  const stagger = (i: number): React.CSSProperties => ({
    opacity: expanded ? 1 : 0,
    transform: expanded ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 500ms ${EASE_SOFT} ${expanded ? 460 + i * 60 : 0}ms, transform 500ms ${EASE_SOFT} ${expanded ? 460 + i * 60 : 0}ms`,
  });

  if (!booking) return null;

  return (
    <div style={{ padding: '36px 28px 0', position: 'relative', zIndex: 1 }}>
      {/* Service */}
      <div style={stagger(0)}>
        <PassLabel theme={theme}>Service</PassLabel>
        <div style={{
          fontFamily: theme.display,
          fontStyle: theme.displayItalic ? 'italic' : 'normal',
          fontWeight: theme.displayWeight,
          fontSize: 30,
          lineHeight: 1.05,
          letterSpacing: '-0.01em',
          marginTop: 6,
        }}>{booking.service}</div>
        {booking.withName && (
          <div style={{ marginTop: 8, fontSize: 14, opacity: 0.72, letterSpacing: '-0.005em' }}>
            with <span style={{ fontWeight: 500 }}>{booking.withName}</span>
          </div>
        )}
      </div>

      {/* Date / Time row */}
      <div style={{ ...stagger(1), marginTop: 30, display: 'flex', gap: 28 }}>
        <div style={{ flex: 1 }}>
          <PassLabel theme={theme}>Date</PassLabel>
          <div style={{
            fontFamily: theme.display,
            fontStyle: theme.displayItalic ? 'italic' : 'normal',
            fontWeight: theme.displayWeight,
            fontSize: 22,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            marginTop: 6,
          }}>{booking.dateNum}</div>
          <div style={{ fontSize: 12.5, opacity: 0.6, marginTop: 3 }}>{booking.dateDay}</div>
        </div>
        <div style={{ flex: 1.2 }}>
          <PassLabel theme={theme}>Time</PassLabel>
          <div style={{
            fontFamily: theme.display,
            fontStyle: theme.displayItalic ? 'italic' : 'normal',
            fontWeight: theme.displayWeight,
            fontSize: 22,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            marginTop: 6,
            fontVariantNumeric: 'tabular-nums',
          }}>{booking.time}</div>
          <div style={{ fontSize: 12.5, opacity: 0.6, marginTop: 3 }}>{booking.duration}</div>
        </div>
      </div>

      {/* Guest — only when a name was entered in the flow */}
      {booking.guest && (
        <div style={{ ...stagger(2), marginTop: 26 }}>
          <PassLabel theme={theme}>Guest</PassLabel>
          <div style={{ fontSize: 16, marginTop: 6, letterSpacing: '-0.005em' }}>{booking.guest}</div>
        </div>
      )}
    </div>
  );
}

// ─── CTAs ─────────────────────────────────────────────────────────

function PassCTAs({ theme, expanded, onAddToCalendar, onManage, booking, businessName }: {
  theme: BookingTheme;
  expanded: boolean;
  onAddToCalendar?: () => void;
  onManage?: () => void;
  booking?: PassBooking | null;
  businessName: string;
}) {
  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!booking) return;
    const text = `Booked: ${booking.service} with ${businessName} — ${booking.dateDay}, ${booking.dateNum} at ${booking.time}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Booking confirmed', text });
      } else {
        await navigator.clipboard.writeText(text);
      }
    } catch {
      // user dismissed the share sheet — nothing to do
    }
  };

  return (
    <div style={{
      padding: '0 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      opacity: expanded ? 1 : 0,
      transform: expanded ? 'translateY(0)' : 'translateY(14px)',
      transition: `opacity 500ms ${EASE_SOFT} ${expanded ? '660ms' : '0ms'}, transform 600ms ${SPRING_SOFT} ${expanded ? '660ms' : '0ms'}`,
      position: 'relative',
      zIndex: 1,
      pointerEvents: expanded ? 'auto' : 'none',
    }}>
      {/* CTAs only when manage and/or calendar handlers are provided.
          Preview passes neither so leads cannot share fake booking data.
          Production BookingFlow always passes calendar (and manage when booked),
          so customer UX matches main for real bookings. */}
      {(onManage || onAddToCalendar) && (
        <>
          {onManage ? (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onManage(); }}
                style={{ ...primaryCtaStyle(theme) }}
              >
                Manage booking
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                {onAddToCalendar && (
                  <button onClick={(e) => { e.stopPropagation(); onAddToCalendar(); }} style={{ ...secondaryCtaStyle(theme), flex: 1 }}>
                    <CalendarGlyph /> Calendar
                  </button>
                )}
                <button onClick={handleShare} style={{ ...secondaryCtaStyle(theme), flex: 1 }}>
                  <ShareGlyph /> Share
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={(e) => { e.stopPropagation(); onAddToCalendar!(); }} style={{ ...primaryCtaStyle(theme), gap: 8 }}>
                <CalendarGlyph /> Add to calendar
              </button>
              <button onClick={handleShare} style={{ ...secondaryCtaStyle(theme), width: '100%' }}>
                <ShareGlyph /> Share
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function primaryCtaStyle(theme: BookingTheme): React.CSSProperties {
  return {
    height: 56,
    borderRadius: 999,
    background: theme.fg,
    color: theme.bg,
    border: 'none',
    fontFamily: theme.ui,
    fontSize: 15.5,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
  };
}

function secondaryCtaStyle(theme: BookingTheme): React.CSSProperties {
  return {
    height: 52,
    borderRadius: 999,
    background: 'transparent',
    color: theme.fg,
    border: `0.5px solid ${theme.border}`,
    fontFamily: theme.ui,
    fontSize: 13.5,
    fontWeight: 500,
    letterSpacing: '-0.005em',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    whiteSpace: 'nowrap',
    WebkitTapHighlightColor: 'transparent',
  };
}

function CalendarGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 1v3M10 1v3M1.5 5.5h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ShareGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.5v8M4 4.5L7 1.5l3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 8v3.5a1 1 0 001 1h7a1 1 0 001-1V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

// ─── PassSurface — texture + content laid out vertically ─────────

function PassSurface({ theme, expanded, onClose, onAddToCalendar, onManage, booking, businessName, businessTagline }: {
  theme: BookingTheme;
  expanded: boolean;
  onClose: () => void;
  onAddToCalendar?: () => void;
  onManage?: () => void;
  booking?: PassBooking | null;
  businessName: string;
  businessTagline?: string | null;
}) {
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: theme.ui,
      color: theme.fg,
    }}>
      {/* very subtle radial wash for depth */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(120% 70% at 50% 0%, ${theme.bgSoft} 0%, ${theme.bg} 55%, ${theme.bg} 100%)`,
        pointerEvents: 'none',
      }} />
      {/* paper grain (very faint) */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1.5px)',
        backgroundSize: '3px 3px',
        opacity: 0.6,
        pointerEvents: 'none',
        mixBlendMode: 'overlay' as const,
      }} />

      {/* status bar gutter (expanded mode) */}
      <div style={{
        height: expanded ? 62 : 24,
        flexShrink: 0,
        transition: `height 700ms ${SPRING_SOFT}`,
      }} />

      {/* HEADER — always visible at top of card */}
      <PassHeader
        theme={theme}
        expanded={expanded}
        onClose={onClose}
        businessName={businessName}
        businessTagline={businessTagline}
        confirmation={booking?.confirmation}
      />

      {/* PERFORATION — visible once content emerges */}
      <div style={{
        marginTop: expanded ? 36 : 28,
        opacity: expanded ? 1 : 0,
        transition: `margin-top 700ms ${SPRING_SOFT}, opacity 400ms ${EASE_SOFT} ${expanded ? '380ms' : '0ms'}`,
        position: 'relative',
        zIndex: 1,
      }}>
        <Perforation theme={theme} />
      </div>

      {/* DETAILS — reveal on expand */}
      <PassDetails theme={theme} expanded={expanded} booking={booking} />

      {/* spacer pushes CTAs to bottom in expanded mode */}
      <div style={{ flex: 1, minHeight: 16 }} />

      {/* CTAS */}
      <PassCTAs
        theme={theme}
        expanded={expanded}
        onAddToCalendar={onAddToCalendar}
        onManage={onManage}
        booking={booking}
        businessName={businessName}
      />

      {/* home indicator gutter */}
      <div style={{
        height: expanded ? 36 : 0,
        flexShrink: 0,
        transition: `height 700ms ${SPRING_SOFT}`,
      }} />
    </div>
  );
}

// ─── Pass — morphs between off-screen / tray / expanded ──────────

export function Pass({ stage, theme, onTap, onClose, onAddToCalendar, onManage, booking, businessName, businessTagline }: PassProps) {
  const expanded = stage === 'expanded';
  const inTray = stage === 'tray';

  // Design geometry (402x874 frame): tray pass top edge sits 426px above
  // the bottom; hidden pass parked 46px below the bottom edge. Width is
  // 402 - 2*31 = 340, centered on wider screens.
  let bounds: { top: string | number; bottom: number; left: string | number; right: string | number; borderRadius: number };
  if (expanded) {
    bounds = { top: 0, bottom: 0, left: 0, right: 0, borderRadius: 0 };
  } else if (inTray) {
    bounds = { top: 'calc(100% - 426px)', bottom: -178, left: 'max(31px, calc(50% - 170px))', right: 'max(31px, calc(50% - 170px))', borderRadius: 28 };
  } else {
    bounds = { top: 'calc(100% + 46px)', bottom: -706, left: 'max(31px, calc(50% - 170px))', right: 'max(31px, calc(50% - 170px))', borderRadius: 28 };
  }

  return (
    <div
      onClick={inTray ? onTap : undefined}
      style={{
        position: 'absolute',
        top: bounds.top,
        bottom: bounds.bottom,
        left: bounds.left,
        right: bounds.right,
        background: theme.bg,
        color: theme.fg,
        borderRadius: bounds.borderRadius,
        overflow: 'hidden',
        boxShadow: expanded
          ? 'none'
          : inTray
            ? '0 -1px 0 rgba(255,255,255,0.6) inset, 0 2px 6px rgba(0,0,0,0.06), 0 18px 40px rgba(28,18,12,0.18), 0 38px 80px rgba(28,18,12,0.14)'
            : 'none',
        transition: [
          `top 560ms ${SPRING}`,
          `bottom 560ms ${SPRING}`,
          `left 500ms ${SPRING_SOFT}`,
          `right 500ms ${SPRING_SOFT}`,
          `border-radius 440ms ${EASE_SOFT}`,
          `box-shadow 400ms ${EASE_SOFT}`,
        ].join(', '),
        zIndex: 30,
        cursor: inTray ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <PassSurface
        onManage={onManage}
        theme={theme}
        expanded={expanded}
        onClose={onClose}
        onAddToCalendar={onAddToCalendar}
        booking={booking}
        businessName={businessName}
        businessTagline={businessTagline}
      />
    </div>
  );
}

// ─── ConfirmScene ─────────────────────────────────────────────────

export function ConfirmScene({
  stage,
  theme,
  visible,
  booking,
  onTap,
  onClose,
  onAddToCalendar,
  onManage,
  businessName,
  businessTagline,
}: ConfirmSceneProps) {
  const expanded = stage === 'expanded';
  const inTray = stage === 'tray';
  const sceneBg = theme.flowBg || FLOW_DEFAULTS.bg;

  return (
    <div
      onClick={inTray ? onTap : undefined}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        background: sceneBg,
        cursor: inTray ? 'pointer' : 'default',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 380ms ease',
      }}
    >
      <ConfirmingState visible={stage === 'confirming'} />
      <TrayChrome visible={inTray} />

      {/* Backdrop — blur + dim the scene as the pass lifts. Tappable when
          expanded so tapping outside the card dismisses it. */}
      <div
        onClick={expanded ? onClose : undefined}
        style={{
          position: 'absolute',
          inset: 0,
          backdropFilter: expanded ? 'blur(8px)' : 'blur(0px)',
          WebkitBackdropFilter: expanded ? 'blur(8px)' : 'blur(0px)',
          background: expanded ? 'rgba(20,16,12,0.14)' : 'rgba(20,16,12,0)',
          transition: 'backdrop-filter 340ms ease, -webkit-backdrop-filter 340ms ease, background 340ms ease',
          zIndex: 29,
          pointerEvents: expanded ? 'auto' : 'none',
        }}
      />

      {/* Pass — sits behind shelf in compact, above in expanded */}
      <Pass
        stage={stage}
        theme={theme}
        onTap={onTap}
        onClose={onClose}
        onAddToCalendar={onAddToCalendar}
        onManage={onManage}
        booking={booking}
        businessName={businessName}
        businessTagline={businessTagline}
      />

      {/* Shelf surface covers below-slot area in compact mode */}
      <Shelf visible={!expanded} bg={sceneBg} />
      <Slot visible={inTray} />
    </div>
  );
}
